/* eslint-disable operator-linebreak */
/* eslint class-methods-use-this: 0 */
/* eslint max-len: 0 */
/* eslint camelcase: 0 */
import { SUPERCHARGER, DESTINATION_CHARGER, MAP } from './Constants';
import AnalyticsUtils from './AnalyticsUtils';

class MapUtils {
  constructor() {
    this.geocoder = {};
  }

  /**
   * @description Pushes user interaction to Google Analytics object
   *
   * @param {Object} e - Event object
   * @param {Object} trtLocation - Address details
   *
   * @return {void}
   */
  chargerAnalytics({ e, trtLocation }) {
    const chargerType = trtLocation ? SUPERCHARGER : DESTINATION_CHARGER;
    const analyticsSource = e.source ? e.source : MAP;
    AnalyticsUtils.pushInteraction({
      interaction: chargerType,
      label: analyticsSource,
    });
  }

  /**
   * @description Plans the map to the specified marker
   *
   * @param {Object} coords - LatLng object
   *
   * @return {void}
   */
  panToMapMarker(coords) {
    const { map } = window;
    map.panTo(coords);
  }

  /**
   * @description Deconstructs the multidimensional Array of lat/long coordinates
   *
   * @param {Array} poly - Coordinates for each arm of the polyline
   *
   * @return {void}
   */
  constructPolyline(poly) {
    const polyLen = poly.length;
    const path = [];
    let i = 0;
    for (i; i < polyLen; i += 1) {
      let x = 0;
      const polyArrLen = poly[i].length;
      for (x; x < polyArrLen; x += 1) {
        path.push({ lat: poly[i][x][0], lng: poly[i][x][1] });
      }
    }

    return path;
  }

  /**
   * @description Lays a polyline on the map
   *
   * @param {Array} path - Lat/Lng coordinates for each arm of the polyline
   *
   * @return {void}
   */
  setPolyline(path) {
    const { google, map } = window;

    if (this.trip) this.deletePolyline();

    this.trip = new google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: '#007FFF',
      strokeOpacity: 1.0,
      strokeWeight: 6,
    });

    this.trip.setMap(map);
  }

  /**
   * @description Fits the map the polyline
   *
   * @return {void}
   */
  fitMapToPolyline() {
    const { google, map } = window;
    const bounds = new google.maps.LatLngBounds();
    const points = this.trip.getPath().getArray();

    let i = 0;
    for (i; i < points.length; i += 1) {
      bounds.extend(points[i]);
    }

    map.fitBounds(bounds);
    this.resizeMap();
  }

  /**
   * @description Fits the map the specified points on the map
   *
   * @param {Array} markers - Collection of markers to fit within the bounds
   * @param {integer=} length - Number of the specified markers should actually be fit to map
   *
   * @return {void}
   */
  fitMapToMarkers(markers, length = false) {
    const { google, map } = window;
    const bounds = new google.maps.LatLngBounds();

    let i = 0;
    const markersLen = length || markers.length;
    for (i; i < markersLen; i += 1) {
      const marker = new google.maps.LatLng(
        markers[i].geoPoint.lat,
        markers[i].geoPoint.lon,
      );
      bounds.extend(marker);
    }

    map.fitBounds(bounds);
  }

  /**
   * @description Deletes the polyline from the map
   *
   * @return {void}
   */
  deletePolyline() {
    if (this.trip) this.trip.setMap(null);
  }

  /**
   * @description Instantiates Google Geocoder class
   *
   * @return {void}
   */
  initGeocoder() {
    const { google } = window;
    this.geocoder = new google.maps.Geocoder();
  }

  /**
   * @description Returns a reference of the Google Geocoder class
   *
   * @return {Object} Instance of geocode for sending geocode requests to Google
   */
  getGeocoder() {
    return this.geocoder;
  }

  /**
   * @description stops user from scrolling off the "edge" of the Earth 🌎
   *
   * @return {void}
   */
  initGravity() {
    const { google, map } = window;
    google.maps.event.addListener(map, 'bounds_changed', () => {
      this.checkBounds(map);
    });
  }

  /**
   * @description Checks if the map has panned out of bounds
   *
   * @param {Object} map - Instance of the Google map
   *
   * @return {void}
   */
  checkBounds(map) {
    const { google } = window;
    if (map) {
      const latNorth = map
        .getBounds()
        .getNorthEast()
        .lat();
      const latSouth = map
        .getBounds()
        .getSouthWest()
        .lat();
      let newLat;

      if (latNorth < 85 && latSouth > -85) return;

      if (latNorth > 85 && latSouth < -85) return;

      if (latNorth > 85) newLat = map.getCenter().lat() - (latNorth - 85);

      if (latSouth < -85) newLat = map.getCenter().lat() - (latSouth + 85);

      if (newLat) {
        const newCenter = new google.maps.LatLng(newLat, map.getCenter().lng());
        map.setCenter(newCenter);
      }
    }
  }

  /**
   * @description Resizes the map
   *
   * @return {void}
   */
  resizeMap() {
    const { google, map } = window;
    google.maps.event.trigger(map, 'resize');
  }

  /**
   * @description Zooms the map to a specified level
   *
   * @param {integer} zoom - zoom level (3-22)
   *
   * @return {void}
   */
  zoomMap(zoom) {
    const { map } = window;
    map.setZoom(zoom);
  }

  /**
   * @description Gets the current map zoom level
   *
   * @return {integer} zoom level (3-22)
   */
  getZoom() {
    const { map } = window;
    return map.getZoom();
  }

  /**
   * @description Centers the map on a point
   *
   * @param {integer} lat - Latitude
   * @param {integer} lng - Longitude
   *
   * @return {void}
   */
  centerMap(lat, lng) {
    const { google, map } = window;
    map.setCenter(new google.maps.LatLng(lat, lng));
  }

  rad(x) {
    return (x * Math.PI) / 180;
  }

  /**
   * @description Calculates the distance between to points on the map
   *
   * @param {Object} p1 - LatLng object
   * @param {Object} p2 - LatLng Object
   *
   * @return {integer} distance in meters
   */
  distanceBetweenPlots(p1, p2) {
    /* eslint no-mixed-operators: 0 */
    const R = 6378137; // Earth’s mean radius in meter
    const dLat = this.rad(p2.lat() - p1.lat());
    const dLong = this.rad(p2.lng() - p1.lng());
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.rad(p1.lat())) *
      Math.cos(this.rad(p2.lat())) *
      Math.sin(dLong / 2) *
      Math.sin(dLong / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c;

    return d;
  }

  /**
   * @description Creates a new LatLng object representing a geographical point on the map
   *
   * @param {integer} lat - Latitude
   * @param {integer} lng - Longitude
   *
   * @return {Object} LatLng object for use with Google Maps API
   */
  latLng(lat, lng) {
    const { google } = window;

    return new google.maps.LatLng(lat, lng);
  }
}

export default new MapUtils();
